/**
 * Pure helpers for building Uflix streaming URLs from movie metadata.
 *
 * These were extracted from `src/main/services/StreamingService.ts` so
 * the future PWA can build the same URLs without pulling in any of the
 * Electron-only network or shell APIs from that service. The desktop
 * app's StreamingService now delegates to these.
 *
 * Anything that touches the network (availability checks, search-result
 * scraping) or the shell (`shell.openExternal`) stays in the desktop
 * service — those have no business running in a browser tab.
 */

export type UflixDomain = 'uflix.cc' | 'uflix.to'

export const UFLIX_DOMAINS: readonly UflixDomain[] = ['uflix.cc', 'uflix.to'] as const

export interface UflixStreamingLink {
  url: string
  type: 'direct' | 'search'
  provider: UflixDomain
}

/**
 * Convert a free-form movie title into the URL-slug shape Uflix expects.
 * Lowercases, strips punctuation, collapses whitespace and dashes.
 *
 * Returns an empty string if the title contains no slug-worthy
 * characters at all (e.g. only emoji or punctuation).
 */
export function uflixTitleSlug(title: string): string {
  if (!title) return ''
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-|-$/g, '')
}

/**
 * Build the direct deep-link URL for a given title/year on a specific
 * Uflix domain. Returns null if the title couldn't be slugified.
 *
 * The `?api=stream4` query parameter is what Uflix uses to render the
 * embedded player view rather than the full site chrome.
 */
export function buildUflixDirectUrl(
  domain: UflixDomain,
  title: string,
  year: number | string | null | undefined
): string | null {
  const slug = uflixTitleSlug(title)
  if (!slug) return null
  const yearPart = year != null && year !== '' ? `-${year}` : ''
  return `https://${domain}/movie/${slug}${yearPart}?api=stream4`
}

/**
 * Build the search-fallback URL for a given title/year on a specific
 * Uflix domain. Always returns a URL — search works even when the
 * direct link wouldn't.
 */
export function buildUflixSearchUrl(
  domain: UflixDomain,
  title: string,
  year: number | string | null | undefined
): string {
  const yearPart = year != null && year !== '' ? ` ${year}` : ''
  const query = encodeURIComponent(`${title}${yearPart}`.trim())
  return `https://${domain}/search?keyword=${query}`
}

/**
 * Build the full set of streaming links (direct + search across all
 * Uflix domains) that the desktop StreamingService used to expose. The
 * PWA can call this and pick whichever link it wants to embed.
 */
export function buildUflixStreamingLinks(
  title: string,
  year: number | string | null | undefined
): UflixStreamingLink[] {
  const links: UflixStreamingLink[] = []
  for (const domain of UFLIX_DOMAINS) {
    const direct = buildUflixDirectUrl(domain, title, year)
    if (direct) {
      links.push({ url: direct, type: 'direct', provider: domain })
    }
    links.push({
      url: buildUflixSearchUrl(domain, title, year),
      type: 'search',
      provider: domain,
    })
  }
  return links
}

/**
 * Build the embed URL the desktop app loads inside its in-app webview
 * for a TMDB id. The PWA can drop this directly into an `<iframe>`.
 *
 * For TV, pass a season + episode to deep-link straight into a specific
 * episode (`/embed/tv/{tmdbId}/{season}/{episode}`); otherwise it goes
 * to the show landing page where the user picks an episode themselves.
 */
export function buildVidsrcEmbedUrl(
  tmdbId: number,
  mediaType: 'movie' | 'tv' = 'movie',
  season?: number,
  episode?: number,
  /**
   * Optional resume position in seconds. The PWA passes this so the
   * player jumps straight to where the user left off. Vidsrc isn't
   * formally documented; we append every plausible name (`t`, `start`,
   * `currentTime`, `time`) so at least one is likely to work, and
   * separately the PWA also fires a postMessage seek command after the
   * iframe loads as a belt-and-braces fallback.
   */
  startSeconds?: number
): string {
  const base =
    mediaType === 'tv' && season != null && episode != null
      ? `https://vsrc.su/embed/tv/${tmdbId}/${season}/${episode}`
      : `https://vsrc.su/embed/${mediaType}/${tmdbId}`

  if (!startSeconds || startSeconds <= 0) return base
  const t = Math.floor(startSeconds)
  const params = new URLSearchParams()
  params.set('t', String(t))
  params.set('start', String(t))
  params.set('currentTime', String(t))
  params.set('time', String(t))
  return `${base}?${params.toString()}`
}
